import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { isEmpty, isEqual, isNumber } from 'radash';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/core/Button';
import { useAttributeFieldPathParser } from '@/components/datamodel/hooks/useAttributeFieldPathParser';
import { useAttributeLabelTranslator } from '@/components/datamodel/hooks/useAttributeLabelTranslator';
import { useAttributesStateStore } from '@/components/datamodel/hooks/useAttributesStore';
import { useValidationsStore, ValidationStatus } from '@/components/datamodel/hooks/useValidationsStore';
import { BaseValidation } from '@/components/datamodel/types';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal';
import { useFormReset } from '@/dialogs/item-editor/Context/FormResetContext';
import { useCRUDView } from '@/hooks/useCRUDView';
import useCustomItemEditorSettings from '@/hooks/useCustomItemEditorSettings';
import { useDataModel } from '@/hooks/useDataModel';
import { clearExcludedFromSaveFields, getExcludedFromSaveFields } from '@/hooks/useExcludedFromSaveFields';
import { useOrganization } from '@/hooks/useOrganization';
import {
    clearGlobalReversedMultiReferenceState,
    getGlobalReversedMultiReferenceState,
} from '@/hooks/useReversedMultiReferenceState';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { appendEscapeParam, encodePathSegment, restUrl } from '@/services/utils';
import {
    DataModelAttributeTypeName,
    Item,
    PersistedItem,
} from '@/types/DataModel';

import { useCreateOrUpdateItem } from '../ItemEditor.queries';

const ITEM_EDITOR_TAB = 'item-editor';

interface EditorDialogButtonsProps {
    closeItemEditor: (item: PersistedItem) => void;
    persistedItem?: PersistedItem;
    currentTab: string;
    isCreatingNewItem: boolean;
    setShowConfirmationModal: Dispatch<SetStateAction<boolean>>;
    onItemSaved?: (item: PersistedItem) => void;
}

const EditorDialogButtons = ({
    currentTab,
    isCreatingNewItem,
    persistedItem,
    onItemSaved,
    closeItemEditor,
    setShowConfirmationModal,
}: EditorDialogButtonsProps) => {
    const { organization } = useOrganization();
    const { user } = useUser();
    const { dataModel } = useDataModel();

    const { toast } = useToast();
    const { customItemEditorSettings } = useCustomItemEditorSettings();
    const queryClient = useQueryClient();

    const { t } = useTranslation();
    const [isSaving, setIsSaving] = useState(false);
    const { getValues, reset } = useFormContext();
    const { isDirty, dirtyFields } = useFormState();
    const formValues = useWatch();
    const [isNewItem, setIsNewItem] = useState(isCreatingNewItem);
    const [itemOrigin, setItemOrigin] = useState(persistedItem);
    const [showSaveConfirmationDialog, setShowSaveConfirmationDialog]= useState(false);
    const [saveConfirmationDialogMsg, setSaveConfirmationDialogMsg]= useState<string | null >();
    const { onAddView, addView, onCancel, onEditView, onEnterViewName } =
        useCRUDView();

    const { setIsResetting } = useFormReset();
    const {
        clearErrors,
        clearWarnings,
        addError,
        addWarning,
        removeErrorByAttributeName,
        removeWarningByAttributeName,
        setValidationStatus,
        localValidationErrors,
    } = useValidationsStore(useShallow(state => ({
        clearErrors: state.clearErrors,
        clearWarnings: state.clearWarnings,
        addError: state.addError,
        addWarning: state.addWarning,
        removeErrorByAttributeName: state.removeErrorByAttributeName,
        removeWarningByAttributeName: state.removeWarningByAttributeName,
        setValidationStatus: state.setStatus,
        localValidationErrors: state.localValidationErrors,
    })));

    const { getNestedFormPathsFromDataModelPath } = useAttributeFieldPathParser();
    const { translateAttributeLabel } = useAttributeLabelTranslator();

    const { initializeWithStates } = useAttributesStateStore(
        useShallow((state) => ({
            initializeWithStates: state.initializeWithStates,
        })),
    );
    const $angularJSInjector = getInjector();
    const $rootScope = getRootScope($angularJSInjector);
    $rootScope.closeItemEditor = closeItemEditor;
    $rootScope.lastItemValid = getValues() as PersistedItem;

    const applySavedAttributeStates = useCallback(
        (attributeStates: string[]) => {
            // Use the new atomic initialization method
            initializeWithStates(attributeStates);
        },
        [initializeWithStates],
    );

    const refreshItemData = useCallback(
        async (itemId: string) => {
            try {
                setIsResetting(true);
                const esc = '~';
                const response = await axios.get(
                    appendEscapeParam(
                        restUrl(`items/${encodePathSegment(itemId, esc)}?validate=true&forceRevalidation=true`),
                        esc, itemId
                    ),
                );
                invalidateQueries();
                const refreshedItem = response.data;

                // Eagerly patch the freshly-fetched item into every cached
                // `findByPrimaryKeys` result so navigation dropdown badges
                // (e.g. `MIXED`, next-lower quantity) reflect the new state
                // immediately on save — without waiting for the async
                // VALIDATION_FINISHED_EVENT roundtrip.
                if (refreshedItem?.primaryKey__) {
                    const findByPrimaryKeysQueries = queryClient.getQueriesData<PersistedItem[]>({
                        queryKey: ['findByPrimaryKeys'],
                    });
                    findByPrimaryKeysQueries.forEach(([key, oldData]) => {
                        if (!oldData || !Array.isArray(oldData)) return;
                        const newData = oldData.map((cachedItem) =>
                            cachedItem?.primaryKey__ === refreshedItem.primaryKey__
                                ? { ...cachedItem, ...refreshedItem }
                                : cachedItem,
                        );
                        queryClient.setQueryData(key, newData);
                    });
                }

                const currentValues = getValues();
                // Create reset values, but preserve MultiReference data for reverse hierarchy attributes
                const resetValues = Object.fromEntries(
                    Object.entries(currentValues).map(([key, currentValue]) => {
                        const refreshedValue = refreshedItem?.[key];
                        // Check if this is a reversed MultiReference attribute
                        const attribute = dataModel?.attribute(key);
                        const isReversedMultiReference =
                            attribute?.params?.reverseHierarchy;
                        // If this is a MultiReference field with reverse hierarchy, preserve the current value
                        if (
                            isReversedMultiReference ||
                            attribute?.typeName ===
                                DataModelAttributeTypeName.MultiReference
                        ) {
                            return [key, currentValue];
                        }
                        // For Dimensional attributes (plain objects), merge so that keys present in currentValue but
                        // absent from refreshedValue are explicitly set to '' instead of being omitted. Omitting a key
                        // causes RHF to fall back to the stored default (the original pre-edit value) for that
                        // registered field
                        if (
                            refreshedValue !== null &&
                            typeof refreshedValue === 'object' &&
                            !Array.isArray(refreshedValue) &&
                            typeof currentValue === 'object' &&
                            !Array.isArray(currentValue) &&
                            currentValue !== null
                        ) {
                            const merged: Record<string, unknown> = { ...refreshedValue };
                            Object.keys(currentValue).forEach(subKey => {
                                if (!(subKey in merged)) {
                                    merged[subKey] = '';
                                }
                            });
                            return [key, merged];
                        }
                        return [key, refreshedValue ?? ''];
                    }),
                );
                setItemOrigin(resetValues as PersistedItem);
                reset(resetValues, { keepDirty: false });

                // Apply attribute states from the refreshed item data
                if (refreshedItem?.attributeStates__) {
                    applySavedAttributeStates(refreshedItem.attributeStates__);
                }

                // Update validation errors and warnings from the refreshed item data
                if (refreshedItem?.__validations__) {
                    const validations = refreshedItem.__validations__ as Record<string, BaseValidation[]>;
                    // Clear all existing errors and warnings first
                    clearErrors();
                    clearWarnings();

                    if (Object.keys(validations).length === 0) {
                        // If there are no validations, set status to IS_COMPLIANT
                        setValidationStatus(ValidationStatus.IS_COMPLIANT);
                    } else {
                        // Set status based on validation_dirty__ flag
                        const initialStatus = refreshedItem?.validation_dirty__
                            ? ValidationStatus.IS_DIRTY
                            : ValidationStatus.IS_NOT_COMPLIANT;
                        setValidationStatus(initialStatus);

                        // Process each attribute's validations
                        Object.keys(validations).forEach(attributeName => {
                            const attributeValidations = validations[attributeName];

                            // Remove existing errors and warnings for this attribute
                            removeErrorByAttributeName(attributeName);
                            removeWarningByAttributeName(attributeName);

                            // Add new validations
                            attributeValidations.forEach(validation => {
                                // Get form paths from the validation path
                                const attributePaths = getNestedFormPathsFromDataModelPath(validation.path);

                                attributePaths.forEach(path => {
                                    if (validation.level === 'Failure' || validation.level === 'Error') {
                                        addError(path, { attributeName, ...validation });
                                    } else {
                                        addWarning(path, { attributeName, ...validation });
                                    }
                                });
                            });
                        });
                    }
                }
            } catch (error) {
                toast.error(t('REFETCH_ERROR_MESSAGE'));
            } finally {
                setTimeout(() => setIsResetting(false), 0);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            getValues,
            reset,
            toast,
            t,
            setIsResetting,
            applySavedAttributeStates,
            clearErrors,
            clearWarnings,
            addError,
            addWarning,
            removeErrorByAttributeName,
            removeWarningByAttributeName,
            setValidationStatus,
            getNestedFormPathsFromDataModelPath,
            dataModel,
        ],
    );

    const { mutate, isPending } = useCreateOrUpdateItem({
        onError: (error) => {
            if (error.response?.data?.message) {
                toast.error(
                    t(
                        error.response.data.message,
                        error.response.data.parameters,
                    ),
                );
            } else {
                toast.error(t('SAVE_ERROR_MESSAGE'));
            }
            setIsSaving(false);
            if (isNewItem) {
                setIsNewItem(true);
            }
        },
        onSuccess: async (item) => {
            if (isCreatingNewItem && isNewItem) {
                setIsNewItem(false);
            }
            await refreshItemData(item.primaryKey__);

            // Clear the global reversed multi-reference state after data refresh
            // This ensures multi-reference components have time to restore their data
            setTimeout(() => {
                clearGlobalReversedMultiReferenceState();
                // Clear excluded from save fields after successful save
                clearExcludedFromSaveFields();
            }, 100);

            onItemSaved?.(item);
            setIsSaving(false);
            toast.success(t('SAVE_SUCCESS_MESSAGE'));
        },
    });

    /**
     * Invalidates all relevant React Query caches after saving an item to ensure
     * the UI displays the most up-to-date data. This function refreshes:
     *
     * - Navigation dropdown menu data (allItemsData) - so the dropdown shows updated item titles
     * - Current item data - so the editor shows the latest item information
     * - Hierarchy data - so navigation between related items works correctly
     * - Items lists - so any item lists in the application are refreshed
     * - Multi-reference components - so parent/child relationships are updated
     * - Item details - so any item detail views show current data
     *
     * This ensures that after saving a new item, both the allItemsData in NavigationDropdownMenu
     * and the current item data are properly refreshed across the entire application.
     */
    const invalidateQueries = () => {
        // Invalidate hierarchy queries to refresh the navigation dropdown
        queryClient.invalidateQueries({ queryKey: ['itemHierarchies'] });
        queryClient.invalidateQueries({ queryKey: ['itemEventHistory'] });
        queryClient.invalidateQueries({ queryKey: ['itemAttributeHistory'] });
        // Invalidate findByPrimaryKeys so navigation dropdown badges derived
        // from item attributes (e.g. `MIXED`, next-lower quantity computed
        // from `logistic_contained_items`) refresh after save. The eager
        // `setQueryData` patch below covers the saved item itself, but
        // related items in the same hierarchy (e.g. children whose parent
        // relation got mutated) also need to be refetched.
        queryClient.invalidateQueries({ queryKey: ['findByPrimaryKeys'] });
    };

    const getAttributeLabelsForLocalValidations = useCallback(() => {
        if (isEmpty(localValidationErrors)) return '';

        const attributeNames = new Set<string>();
        Object.values(localValidationErrors).forEach(validations => {
            validations.forEach(validation => {
                if (validation.attributeName) {
                    attributeNames.add(validation.attributeName);
                }
            });
        });

        let attributeLabels = '';
        attributeNames.forEach(attributeName => {
            const attribute = dataModel?.attribute(attributeName);
            if (attribute) {
                const translatedLabel = translateAttributeLabel(attribute);
                attributeLabels += `<li>${translatedLabel}</li>`;
            }
        });

        return attributeLabels;
    }, [localValidationErrors, dataModel, translateAttributeLabel]);

    const buildFinalItemPayload = useCallback((values: Item): Item => {
        const globalReversedState = getGlobalReversedMultiReferenceState();
        const excludedFields = getExcludedFromSaveFields();

        // Start from form values
        const finalItem: Item = { ...values };

        // include action__ flags
        if (globalReversedState.length > 0) {
            globalReversedState.forEach((fieldGroup) => {
                Object.keys(fieldGroup).forEach((fieldPath) => {
                    const fieldChanges = fieldGroup[fieldPath];
                    if (Array.isArray(fieldChanges) && fieldChanges.length > 0) {
                        (finalItem as any)[fieldPath] = fieldChanges;
                    }
                });
            });
        }

        excludedFields.forEach((fieldPath) => {
            delete (finalItem as any)[fieldPath];
        });

        return finalItem;
    }, []);

    const handelSavingItem = () => {

        // Check for local validation errors and block saving if they exist
        if (!isEmpty(localValidationErrors)) {
            const attributeLabels = getAttributeLabelsForLocalValidations();

            const errorMessage = attributeLabels
                ? `${t('LOCAL_VALIDATION.CAN_NOT_SAVE_ITEM')}<ul className="text-left">${attributeLabels}</ul>`
                : t('LOCAL_VALIDATION.CAN_NOT_SAVE_ITEM');

            toast.error(errorMessage);
            return;
        }

        const values = getValues() as Item;
        const newItem = buildFinalItemPayload(values);
        const saveConfirmationDialogMsg = customItemEditorSettings?.saveConfirmationDialogMsg?.(
            newItem,
            itemOrigin,
            user,
            organization,
        );

        if (!isEmpty(saveConfirmationDialogMsg)) {
            setSaveConfirmationDialogMsg(saveConfirmationDialogMsg);
            setShowSaveConfirmationDialog(true);
        } else {
            saveItem();
        }
    };

    const saveItem = () => {
        setShowSaveConfirmationDialog(false);
        setIsSaving(true);
        const isUnique = isNewItem;
        const values = getValues() as Item;
        const finalItem = buildFinalItemPayload(values);

        mutate({
            merge: true,
            unique: isUnique,
            item: finalItem
        });
    };

    const hasFormChanges = useMemo(() => {
        if (!persistedItem) return isDirty;
        if (!isDirty) return false;

        return Object.keys(dirtyFields).some(key => {
            const currentValue = formValues[key];
            const originalValue = (persistedItem as Record<string, unknown>)[key];
            return !isEqual(currentValue, originalValue);
        });
    }, [formValues, persistedItem, isDirty, dirtyFields]);

    const showViewBtn = onAddView || isNumber(onEditView);
    const handleCancel = () => {
        if (hasFormChanges) {
            setShowConfirmationModal(true);
        } else {
            closeItemEditor(getValues() as PersistedItem);
        }
    };
    const isSaveDisabled =
        isSaving ||
        !hasFormChanges ||
        isPending ||
        currentTab !== ITEM_EDITOR_TAB;

    return (
        <div className="tw-flex tw-justify-end tw-gap-2">
            {showViewBtn ? (
                <>
                    <Button
                        type="button"
                        variant="default"
                        onClick={() => addView()}
                        loading={isPending}
                    >
                        {onAddView ? t('VIEW.SAVE') : t('VIEW.SAVE_CHANGES')}
                    </Button>
                    {isNumber(onEditView) && !onEnterViewName && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => addView(true)}
                            loading={isPending}
                        >
                            {t('VIEW.SAVE_AS')}
                        </Button>
                    )}
                    <Button
                        variant="secondary"
                        type="button"
                        disabled={isPending}
                        onClick={() => onCancel()}
                    >
                        {t('CLOSE')}
                    </Button>
                </>
            ) : (
                <>
                    <Button
                        type="button"
                        id="button-submit"
                        variant="default"
                        onClick={handelSavingItem}
                        disabled={isSaveDisabled}
                        loading={isPending}
                    >
                        {isNewItem ? t('ITEM.CREATE') : t('ITEM.SAVE_CHANGES')}
                    </Button>
                    <Button
                        variant="secondary"
                        className="close-item-editor"
                        id="close-item-editor"
                        type="button"
                        disabled={isPending}
                        onClick={handleCancel}
                    >
                        {t('CLOSE')}
                    </Button>
                </>
            )}
            {!isEmpty(saveConfirmationDialogMsg) && <ConfirmationModal
                title={t('ITEM.SAVE_CHANGES_CONFIRM')}
                subTitle={saveConfirmationDialogMsg || ''}
                closeBtnText={t('CANCEL')}
                show={showSaveConfirmationDialog}
                onSubmit={()=>saveItem()}
                isOnSave= {true}
                onClose={()=>setShowSaveConfirmationDialog(false)}
            />}
        </div>
    );
};

EditorDialogButtons.displayName = 'EditorDialogButtons';

export { EditorDialogButtons };
